// components/RecommendationsPanel.js
import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Badge
} from '@material-ui/core';
import {
  FlashOn,
  TrendingUp,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Schedule,
  AttachMoney,
  Group,
  NavigateNext
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: '#f8f9fa'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  recommendationCard: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)'
    }
  },
  highPriority: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: '#fff5f5'
  },
  mediumPriority: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: '#fffaf0'
  },
  priorityBadge: {
    marginLeft: theme.spacing(1)
  },
  impactChip: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  actionButton: {
    marginTop: theme.spacing(1)
  },
  expandedContent: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

const RecommendationsPanel = ({ recommendations, onActionClick }) => {
  const classes = useStyles();
  const [expandedItems, setExpandedItems] = useState({});
  const [completedActions, setCompletedActions] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleActionClick = (recommendation, actionType) => {
    if (onActionClick) {
      onActionClick(recommendation, actionType);
    }
    // Mark as completed
    setCompletedActions(prev => new Set([...prev, recommendation.type]));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'workload_rebalance': return <Group color="primary" />;
      case 'territory_redesign': return <TrendingUp color="primary" />;
      case 'overtime_reduction': return <Schedule color="error" />;
      case 'route_optimization': return <FlashOn color="primary" />;
      case 'schedule_leveling': return <Schedule color="primary" />;
      default: return <TrendingUp color="primary" />;
    }
  };

  const allRecommendations = [
    ...(recommendations.immediate || []),
    ...(recommendations.optimization || [])
  ];

  if (allRecommendations.length === 0) {
    return null;
  }

  const immediateCount = recommendations.immediate?.length || 0;
  const optimizationCount = recommendations.optimization?.length || 0;

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">
            Recommended Actions
          </Typography>
          <Badge 
            badgeContent={immediateCount} 
            color="error" 
            className={classes.priorityBadge}
            invisible={immediateCount === 0}
          >
            <Chip
              size="small"
              label="Immediate"
              color="secondary"
              style={{ marginLeft: 16 }}
            />
          </Badge>
          <Badge 
            badgeContent={optimizationCount} 
            color="primary" 
            className={classes.priorityBadge}
            invisible={optimizationCount === 0}
          >
            <Chip
              size="small"
              label="Optimize"
              style={{ marginLeft: 8 }}
            />
          </Badge>
        </Box>
      </div>

      <List>
        {allRecommendations.map((rec, index) => {
          const isExpanded = expandedItems[rec.type];
          const isCompleted = completedActions.has(rec.type);
          const isHighPriority = rec.priority === 'high';

          return (
            <React.Fragment key={rec.type}>
              <Paper 
                className={`${classes.recommendationCard} ${
                  isHighPriority ? classes.highPriority : classes.mediumPriority
                }`}
                onClick={() => toggleExpanded(rec.type)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box display="flex" alignItems="flex-start" flex={1}>
                    <ListItemIcon>
                      {isCompleted ? (
                        <CheckCircle style={{ color: '#4caf50' }} />
                      ) : (
                        getIcon(rec.type)
                      )}
                    </ListItemIcon>
                    <Box flex={1}>
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                        {rec.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {rec.description}
                      </Typography>
                      
                      {/* Impact Chips */}
                      <Box mt={1}>
                        {rec.potentialImpact && (
                          <Chip
                            size="small"
                            label={rec.potentialImpact}
                            className={classes.impactChip}
                            icon={<TrendingUp />}
                          />
                        )}
                        {rec.estimatedSavings && (
                          <Chip
                            size="small"
                            label={rec.estimatedSavings}
                            className={classes.impactChip}
                            icon={<AttachMoney />}
                            color="primary"
                          />
                        )}
                        {rec.quickWin && (
                          <Chip
                            size="small"
                            label="Quick Win"
                            className={classes.impactChip}
                            style={{ backgroundColor: '#4caf50', color: 'white' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <IconButton size="small">
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <div className={classes.expandedContent}>
                    <Typography variant="body2" paragraph>
                      <strong>Action Required:</strong> {rec.action}
                    </Typography>
                    
                    {rec.affectedCrews && (
                      <Box mb={2}>
                        <Typography variant="caption" color="textSecondary">
                          Affected Crews:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {rec.affectedCrews.map(crew => (
                            <Chip
                              key={crew}
                              size="small"
                              label={crew}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(rec, 'execute');
                        }}
                        disabled={isCompleted}
                        endIcon={<NavigateNext />}
                      >
                        {isCompleted ? 'Completed' : 'Take Action'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(rec, 'details');
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </div>
                </Collapse>
              </Paper>
              {index < allRecommendations.length - 1 && <Divider style={{ margin: '8px 0' }} />}
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};

export default RecommendationsPanel;